import { useCallback, useState } from 'react';
import type { ListingDetails } from 'app/types/listingDetails.type';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { HaloSpokeHeader } from '../HaloSpokeHeader';
import { TourSpoke } from './TourSpoke';

/**
 * Hook that provides handlers to open the tour flow from anywhere.
 *
 * Opens TourSpoke in a popup modal (FullscreenOverlay) on all viewports.
 * Caller must render `tourOverlay` in their JSX.
 *
 * openTour — starts fresh at SELECT_TOUR_TYPE (for booking a new tour).
 * openTourReschedule — starts at MODIFY_TOUR (shows existing tour + cancel option).
 */
export const useTourAction = (listing?: ListingDetails) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModifyingTour, setIsModifyingTour] = useState(false);

  const openTour = useCallback(() => {
    setIsModifyingTour(false);
    setIsOpen(true);
  }, []);

  const openTourReschedule = useCallback(() => {
    setIsModifyingTour(true);
    setIsOpen(true);
  }, []);

  const tourOverlay = (
    <FullscreenOverlay.Root open={isOpen} onOpenChange={setIsOpen}>
      <FullscreenOverlay.Portal>
        <FullscreenOverlay.Backdrop>
          <FullscreenOverlay.Content>
            <HaloSpokeHeader />
            <FullscreenOverlay.Body padding="edge-to-edge">
              <TourSpoke onClose={() => setIsOpen(false)} listing={listing} isModifyingTour={isModifyingTour} />
            </FullscreenOverlay.Body>
          </FullscreenOverlay.Content>
        </FullscreenOverlay.Backdrop>
      </FullscreenOverlay.Portal>
    </FullscreenOverlay.Root>
  );

  return { openTour, openTourReschedule, tourOverlay };
};
